import { format } from 'date-fns';
import { FilterState } from '../pages/SearchPage/contexts/types';
import { storeCaseDraft } from './caseDraftStorage';

interface SearchFiltersToFormParams {
  filters: FilterState;
  keywords: string | null;
  startDate: Date | null;
  endDate: Date | null;
  isMultilingual: boolean;
}

// CloudFront rejects request URLs over 8,192 bytes with a 414, and the query
// string shares that budget with the origin, path and other params. Above this
// conservative threshold the params are handed off via localStorage instead
// (see caseDraftStorage.ts) and the URL carries only a short `form_ref` key.
const MAX_INLINE_PARAMS_LENGTH = 2000;

/**
 * Converts SearchPage filter state to URL parameters for NarrativeForm prefilling.
 * Uses 'form_' prefix to distinguish from search page params.
 * Long payloads are stored in localStorage and referenced via `form_ref`.
 */
export function createNarrativeFormURL(params: SearchFiltersToFormParams): string {
  const { filters, keywords, startDate, endDate, isMultilingual } = params;
  const urlParams = new URLSearchParams();

  if (keywords && keywords.trim().length > 0) {
    urlParams.set('form_keywords',keywords);
  }

  if (startDate) {
    urlParams.set('form_start_date', format(startDate, 'yyyy-MM-dd'));
  }
  if (endDate) {
    urlParams.set('form_end_date', format(endDate, 'yyyy-MM-dd'));
  }

  if (isMultilingual) {
    urlParams.set('form_multilingual', 'true');
  }

  if (filters.platforms.value.length > 0) {
    filters.platforms.value.forEach((platform) => {
      urlParams.append('form_platforms', String(platform));
    });
  }

  if (filters.countries.value.length > 0) {
    filters.countries.value.forEach((country) => {
      urlParams.append('form_countries', country);
    });
    if (filters.countries.exclude) {
      urlParams.set('form_countries_exclude', 'true');
    }
  }

  if (filters.languages.value.length > 0) {
    filters.languages.value.forEach((language) => {
      urlParams.append('form_languages', String(language));
    });
    if (filters.languages.exclude) {
      urlParams.set('form_languages_exclude', 'true');
    }
  }

  if (filters.sourceGroups.value.length > 0) {
    filters.sourceGroups.value.forEach((sourceGroup) => {
      urlParams.append('form_source_groups', String(sourceGroup));
    });
    if (filters.sourceGroups.exclude) {
      urlParams.set('form_source_groups_exclude', 'true');
    }
  }

  if (filters.sources.value.length > 0) {
    filters.sources.value.forEach((source) => {
      urlParams.append('form_sources', String(source));
    });
    if (filters.sources.exclude) {
      urlParams.set('form_sources_exclude', 'true');
    }
  }

  if (filters.sentiment.value.length > 0) {
    filters.sentiment.value.forEach((sentiment) => {
      urlParams.append('form_sentiment', sentiment);
    });
  }

  if (filters.contentTypes.value.length > 0) {
    filters.contentTypes.value.forEach((contentType) => {
      urlParams.append('form_content_types', contentType);
    });
  }

  if (filters.bots.value === true) {
    urlParams.set('form_show_bots', 'true');
  }
  if (filters.bots.exclude === true) {
    urlParams.set('form_bots_exclude', 'true');
  }

  if (filters.discreditations.value.length > 0) {
    filters.discreditations.value.forEach((discreditation) => {
      urlParams.append('form_discreditations', discreditation);
    });
    if (filters.discreditations.exclude) {
      urlParams.set('form_discreditations_exclude', 'true');
    }
  }

  if (filters.affiliations.value.length > 0) {
    if (filters.affiliations.value[0] === 'all') {
      urlParams.set('form_affiliations', 'all');
    } else {
      filters.affiliations.value.forEach((affiliation) => {
        urlParams.append('form_affiliations', affiliation);
      });
    }
    if (filters.affiliations.exclude) {
      urlParams.set('form_affiliations_exclude', 'true');
    }
  }

  if (filters.feeds.value.length > 0) {
    filters.feeds.value.forEach((feed) => {
      urlParams.append('form_feeds', String(feed));
    });
    if (filters.feeds.exclude) {
      urlParams.set('form_feeds_exclude', 'true');
    }
  }

  if (filters.geoGroup.value.length > 0) {
    filters.geoGroup.value.forEach((geoGroup) => {
      urlParams.append('form_geo_group', String(geoGroup));
    });
    if (filters.geoGroup.exclude) {
      urlParams.set('form_geo_group_exclude', 'true');
    }
  }

  if (filters.tags.value.length > 0) {
    filters.tags.value.forEach((tag) => {
      urlParams.append('form_tags', tag);
    });
    if (filters.tags.exclude) {
      urlParams.set('form_tags_exclude', 'true');
    }
  }

  if (filters.affiliationTypes.value.length > 0) {
    filters.affiliationTypes.value.forEach((type) => {
      urlParams.append('form_affiliation_types', type);
    });
    if (filters.affiliationTypes.exclude) {
      urlParams.set('form_affiliation_types_exclude', 'true');
    }
  }

  const serialized = urlParams.toString();

  if (serialized.length > MAX_INLINE_PARAMS_LENGTH) {
    const draftKey = storeCaseDraft(serialized);
    if (draftKey) {
      const refParams = new URLSearchParams();
      refParams.set('form_ref', draftKey);
      return refParams.toString();
    }
    // localStorage unavailable — fall through to the full inline params as a
    // last resort (short queries still work; very long ones may still 414).
  }

  return serialized;
}
